// Hip Joint Template Configuration
// Questions simplified to 12-year-old reading level

export const hipTemplate = {
  id: 'HIP',
  name: 'Hip',
  icon: 'ðŸ¦´',
  dominantSide: 'leg',

  fields: {
    injuredSide: {
      type: 'radio',
      label: 'Which hip has the problem?',
      options: ['Right', 'Left', 'Both'],
      required: true
    },

    problemDate: {
      type: 'date',
      label: 'When did your hip problem start?',
      required: true
    },

    onsetType: {
      type: 'radio',
      label: 'How did it start?',
      options: ['Suddenly', 'Slowly over time'],
      required: true
    },

    mainProblem: {
      type: 'checkbox-multiple',
      label: 'What problems are you having? (tick all that apply)',
      options: [
        'Pain',
        'Stiffness',
        'Limping',
        'Weakness',
        'Clicking or catching',
        'Feeling like it might give way',
        'Change in leg length',
        'Other'
      ]
    },

    mainProblemOther: {
      type: 'text',
      label: 'Please describe the other problem:',
      placeholder: 'Tell us about your other problem',
      showWhen: { field: 'mainProblem', includes: 'Other' }
    },

    problemDescription: {
      type: 'textarea',
      label: 'Tell us what happened:',
      placeholder: 'Describe how your hip problem started',
      rows: 3,
      required: true
    },

    // Pain Section
    painLocation: {
      type: 'checkbox-multiple',
      label: 'Where exactly do you feel pain?',
      options: [
        'Front of hip (groin area)',
        'Side of hip',
        'Back of hip (buttock)',
        'Thigh',
        'Knee (hip problems can cause knee pain)'
      ]
    },

    painType: {
      type: 'checkbox-multiple',
      label: 'What does your pain feel like?',
      options: ['Dull', 'Sharp', 'Aching', 'Throbbing', 'Other']
    },

    painTypeOther: {
      type: 'text',
      label: 'Please describe the pain:',
      placeholder: 'Tell us what your pain feels like',
      showWhen: { field: 'painType', includes: 'Other' }
    },

    painSeverity: {
      type: 'radio',
      label: 'How bad is your pain? (0 = no pain, 10 = worst pain ever)',
      options: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']
    },

    painRadiation: {
      type: 'radio',
      label: 'Does the pain spread anywhere else?',
      options: ['Yes, to my thigh', 'Yes, to my knee', 'Yes, to my back', 'No, just my hip']
    },

    painTiming: {
      type: 'checkbox-multiple',
      label: 'When is your pain worst?',
      options: [
        'All the time',
        'When walking',
        'When standing',
        'At night',
        'In the morning',
        'After sitting for a while'
      ]
    },

    painWorsening: {
      type: 'checkbox-multiple',
      label: 'What makes your pain worse?',
      options: [
        'Walking',
        'Standing',
        'Squatting',
        'Sitting',
        'Climbing stairs',
        'Getting in/out of car',
        'Other'
      ]
    },

    painWorseningOther: {
      type: 'text',
      label: 'What else makes your pain worse?',
      placeholder: 'Tell us what else makes your pain worse',
      showWhen: { field: 'painWorsening', includes: 'Other' }
    },

    painRelieving: {
      type: 'checkbox-multiple',
      label: 'What helps your pain feel better?',
      options: [
        'Rest',
        'Changing position',
        'Pain medication',
        'Heat',
        'Other'
      ]
    },

    painRelievingOther: {
      type: 'text',
      label: 'What else helps your pain?',
      placeholder: 'Tell us what else helps your pain',
      showWhen: { field: 'painRelieving', includes: 'Other' }
    },

    // Stiffness Section
    hasStiffness: {
      type: 'radio',
      label: 'Is your hip stiff?',
      options: ['Yes', 'No']
    },

    stiffnessMovements: {
      type: 'checkbox-multiple',
      label: 'Which movements are stiff or limited?',
      options: [
        'Bending hip up',
        'Straightening hip',
        'Turning leg in',
        'Turning leg out',
        'Moving leg out to the side'
      ],
      showWhen: { field: 'hasStiffness', equals: 'Yes' }
    },

    stiffnessChange: {
      type: 'radio',
      label: 'Is the stiffness:',
      options: ['Getting better', 'Getting worse', 'Staying the same'],
      showWhen: { field: 'hasStiffness', equals: 'Yes' }
    },

    // Walking/Limp
    hasLimp: {
      type: 'radio',
      label: 'Do you walk with a limp?',
      options: ['Yes', 'No', 'Sometimes']
    },

    limpOnset: {
      type: 'radio',
      label: 'How long have you been limping?',
      options: ['Since it started', 'It\'s getting worse', 'Only when it\'s really sore'],
      showWhen: { field: 'hasLimp', equals: 'Yes' }
    },

    useWalkingAid: {
      type: 'radio',
      label: 'Do you use a walking stick, crutches, or walking frame?',
      options: ['Yes', 'No']
    },

    // Mechanical Symptoms
    hasClicking: {
      type: 'radio',
      label: 'Does your hip click or catch?',
      options: ['Yes', 'No']
    },

    hasLocking: {
      type: 'radio',
      label: 'Does your hip ever lock up or get stuck?',
      options: ['Yes', 'No']
    },

    hasGivingWay: {
      type: 'radio',
      label: 'Does your hip feel unstable or give way?',
      options: ['Yes', 'No']
    },

    // Functional Limitations
    sittingCrossLegged: {
      type: 'radio',
      label: 'Can you sit cross-legged on the floor?',
      options: ['Yes', 'With difficulty', 'No']
    },

    squatting: {
      type: 'radio',
      label: 'Can you squat down?',
      options: ['Yes', 'With difficulty', 'No']
    },

    stairsDifficulty: {
      type: 'radio',
      label: 'Can you climb stairs?',
      options: ['Yes', 'Yes but it\'s hard', 'No']
    },

    walkingDistance: {
      type: 'radio',
      label: 'How far can you walk?',
      options: [
        'As far as I want',
        'Limited - I need to rest after a while',
        'Only short distances',
        'I can barely walk'
      ]
    },

    shoesAndSocks: {
      type: 'radio',
      label: 'Can you put on your shoes and socks?',
      options: ['Yes, no problem', 'It\'s difficult', 'I need help']
    },

    gettingInOutCar: {
      type: 'radio',
      label: 'Can you get in and out of a car easily?',
      options: ['Yes', 'It\'s difficult', 'Very difficult']
    },

    // Shortening/Deformity
    legLengthDifference: {
      type: 'radio',
      label: 'Do you think one leg is shorter than the other?',
      options: ['Yes', 'No', 'Not sure']
    },

    hipDeformity: {
      type: 'radio',
      label: 'Have you noticed any change in your hip shape or position?',
      options: ['Yes', 'No']
    },

    // Previous History
    previousHipProblems: {
      type: 'radio',
      label: 'Have you had hip problems before?',
      options: ['Yes', 'No']
    },

    previousHipDetails: {
      type: 'textarea',
      label: 'Tell us about your previous hip problems:',
      rows: 2,
      showWhen: { field: 'previousHipProblems', equals: 'Yes' }
    },

    previousHipSurgery: {
      type: 'radio',
      label: 'Have you had hip surgery before?',
      options: ['Yes', 'No']
    },

    childhoodHipProblems: {
      type: 'radio',
      label: 'Did you have any hip problems as a child?',
      options: ['Yes', 'No', 'Not sure']
    },

    childhoodDetails: {
      type: 'text',
      label: 'What hip problems did you have as a child?',
      showWhen: { field: 'childhoodHipProblems', equals: 'Yes' }
    },

    lowBackPain: {
      type: 'radio',
      label: 'Do you have lower back pain as well?',
      options: ['Yes', 'No']
    },

    // Medical History
    medicalConditions: {
      type: 'checkbox-multiple',
      label: 'Do you have any of these conditions?',
      options: [
        'Rheumatoid arthritis',
        'Osteoarthritis',
        'Osteoporosis',
        'Diabetes',
        'Ankylosing spondylitis',
        'None of these'
      ]
    },

    currentMedications: {
      type: 'textarea',
      label: 'What medications do you take?',
      placeholder: 'List all your current medications',
      rows: 2
    },

    allergies: {
      type: 'text',
      label: 'Do you have any allergies?',
      placeholder: 'List any allergies'
    },

    // Lifestyle
    occupation: {
      type: 'text',
      label: 'What is your job?',
      placeholder: 'Tell us what you do for work'
    },

    occupationType: {
      type: 'radio',
      label: 'Does your job involve:',
      options: ['Standing a lot', 'Heavy lifting', 'Squatting', 'Sitting most of the day', 'Mix of activities']
    },

    sport: {
      type: 'text',
      label: 'What sports or exercise do you do?',
      placeholder: 'e.g., running, football, dancing, gym'
    },

    smoking: {
      type: 'radio',
      label: 'Do you smoke?',
      options: ['No, never smoked', 'I used to smoke', 'Yes, I smoke']
    }
  },

  sections: [
    {
      id: 'consent',
      title: '0. Consent & Agreements',
      component: 'ConsentSection',
      enabled: true
    },
    {
      id: 'demographics',
      title: '1. Your Details',
      component: 'DemographicsSection',
      enabled: true
    },
    {
      id: 'problem',
      title: '2. About Your Hip Problem',
      component: 'GenericSection',
      enabled: true,
      fields: ['injuredSide', 'problemDate', 'onsetType', 'mainProblem', 'mainProblemOther', 'problemDescription']
    },
    {
      id: 'pain',
      title: '3. Your Pain',
      component: 'GenericSection',
      enabled: true,
      fields: ['painLocation', 'painType', 'painTypeOther', 'painSeverity', 'painRadiation', 'painTiming', 'painWorsening', 'painWorseningOther', 'painRelieving', 'painRelievingOther']
    },
    {
      id: 'stiffness',
      title: '4. Stiffness',
      component: 'GenericSection',
      enabled: true,
      fields: ['hasStiffness', 'stiffnessMovements', 'stiffnessChange']
    },
    {
      id: 'walking',
      title: '5. Walking & Limp',
      component: 'GenericSection',
      enabled: true,
      fields: ['hasLimp', 'limpOnset', 'useWalkingAid']
    },
    {
      id: 'mechanical',
      title: '6. How Your Hip Feels',
      component: 'GenericSection',
      enabled: true,
      fields: ['hasClicking', 'hasLocking', 'hasGivingWay', 'legLengthDifference', 'hipDeformity']
    },
    {
      id: 'functional',
      title: '7. What You Can & Can\'t Do',
      component: 'GenericSection',
      enabled: true,
      fields: ['sittingCrossLegged', 'squatting', 'stairsDifficulty', 'walkingDistance', 'shoesAndSocks', 'gettingInOutCar']
    },
    {
      id: 'history',
      title: '8. Previous Problems',
      component: 'GenericSection',
      enabled: true,
      fields: ['previousHipProblems', 'previousHipDetails', 'previousHipSurgery', 'childhoodHipProblems', 'childhoodDetails', 'lowBackPain']
    },
    {
      id: 'medical',
      title: '9. Your Health & Lifestyle',
      component: 'GenericSection',
      enabled: true,
      fields: ['medicalConditions', 'currentMedications', 'allergies', 'occupation', 'occupationType', 'sport', 'smoking']
    },{
      id: 'exercise',
      title: '10. Exercise as a Vital Sign',
      component: 'ExerciseVitalSignSection',
      enabled: true
    },
    
    {
      id: 'goals',
      title: '11. Your Goals',
      component: 'GoalsSection',
      enabled: true
    }
  ],

  letterTemplate: {
    mechanismLanguage: {
      'Suddenly': 'acute onset',
      'Slowly over time': 'gradual onset'
    },
    commonDiagnoses: [
      'Hip osteoarthritis',
      'Femoroacetabular impingement (FAI)',
      'Hip labral tear',
      'Greater trochanteric pain syndrome',
      'Hip bursitis',
      'Adductor strain',
      'Hip flexor strain',
      'Referred pain from lumbar spine'
    ]
  }
};

export default hipTemplate;
